import React, { Fragment, lazy, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { matchPath, Route, Routes, useLocation, useNavigate } from 'react-router';
import qs from 'query-string';

import { LAYOUT_TYPE_IN_POPUP } from './common/constants/lw';
import {
  ALL_EXISTING_ROUTES,
  CHANNEL_VIEW_PATH,
  DEFAULT_VIEW_PATH,
  SEARCH_AI_VIEW_PATH,
  SEARCH_PATH,
  SEARCH_VIEW_PATH,
  SHARE_VIEW_PATH,
  SINGLE_VIEW_PATH,
} from './common/constants/routePath';
import { REACT_APP_VERSION } from '@/modules/common/constants/envs';

import { useVerifyChipInUrl } from './common/components/Chips/helpers/useVerifyChipInUrl';
import { handlePublisherAnchor, scrollToPublisherAnchor } from './common/helpers/dom';
import { destroyHistoryRoutePath, processHistoryObject, updateHistoryRoutePath } from './common/helpers/history';
import logger from './common/helpers/logger';
import * as commonSelectors from './common/redux/selectors/common';
import { getValidQuery, isEqualAllQueryObjects } from './Search/helpers/query';
import { isInSameDomain, isWatchInIframe, updateIframeRouter } from '@/modules/common/helpers/routes';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import DefaultSuspense from './common/components/DefaultSuspense/DefaultSuspense';
import PagePopup from './common/components/PagePopup/PagePopup';
import AllPlayersProvider from './common/components/Player/AllPlayersProvider';
import ChannelExistsValidation from './common/components/ValidChannel/ValidChannel';

import styles from './App.module.scss';

const SingleOverviewPage = lazy(() => import('./PlayerOverview/Single'));
const SearchOverviewPage = lazy(() => import('./PlayerOverview/Search'));
const DefaultOverviewPage = lazy(() => import('./PlayerOverview/Default'));
const ShareOverviewPage = lazy(() => import('./PlayerOverview/Share'));
const SearchAiOverviewPage = lazy(() => import('./PlayerOverview/SearchAi'));
const SearchPage = lazy(() => import('./Search/Search'));
const ChannelListPage = lazy(() => import('./ChannelList/ChannelList'));
const MobileSearchButton = lazy(() => import('./common/components/MobileSearchButton/MobileSearchButton'));
const ChannelView = lazy(() => import('./ChannelView/ChannelView'));

interface RouteItem {
  path: string;
  component: React.ReactElement;
}

// added to see version in PageSpeed Insights
const showVersion = ['localhost:3000', 'acdev-watch-dev-perf.dev-anyclip.com'].includes(window.location.host);

function AppComponent() {
  useVerifyChipInUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const layout = useSelector(commonSelectors.layoutSelector);
  const search = useSelector(commonSelectors.searchSelector);
  const genAI = useSelector(commonSelectors.genAISelector);
  const iframeRouter = useSelector(commonSelectors.iframeRouterSelector);

  const lwInPopupType = layout === LAYOUT_TYPE_IN_POPUP;

  useLayoutEffect(() => {
    const currentRoute = ALL_EXISTING_ROUTES.find((route) => matchPath(route, location.pathname)) ?? null;

    handlePublisherAnchor();
    scrollToPublisherAnchor();

    if (isWatchInIframe && isInSameDomain && iframeRouter) {
      logger.log('Set new iframe router state');
      updateIframeRouter(currentRoute ? window.location.hash : '');
    }

    // todo: move to the HistoryPathProvider (replace direct variables)
    updateHistoryRoutePath(currentRoute);
  }, [location.pathname, location.search, location.hash]);

  useLayoutEffect(
    () => () => {
      destroyHistoryRoutePath();
    },
    [],
  );

  useLayoutEffect(() => {
    const rawQuery = qs.parse(location.search);
    const filteredQuery = getValidQuery(rawQuery);

    if (!isEqualAllQueryObjects(rawQuery, filteredQuery)) {
      navigate(processHistoryObject({ search: filteredQuery }), { replace: true });
    }
  }, [location.search]);

  const switchRouterList: RouteItem[] = [
    search && {
      path: SEARCH_VIEW_PATH,
      component: <SearchOverviewPage />,
    },
    search && {
      path: SEARCH_PATH,
      component: <SearchPage />,
    },
    {
      path: SINGLE_VIEW_PATH,
      component: <SingleOverviewPage />,
    },
    {
      path: SHARE_VIEW_PATH,
      component: <ShareOverviewPage />,
    },
    genAI && {
      path: SEARCH_AI_VIEW_PATH,
      component: <SearchAiOverviewPage />,
    },
    {
      path: DEFAULT_VIEW_PATH,
      component: (
        <ChannelExistsValidation>
          <DefaultOverviewPage />
        </ChannelExistsValidation>
      ),
    },
    {
      path: CHANNEL_VIEW_PATH,
      component: (
        <ChannelExistsValidation>
          <ChannelView />
        </ChannelExistsValidation>
      ),
    },
  ]
    .filter((route): route is RouteItem => Boolean(route))
    .map(({ path, component }) => {
      const Wrapper = lwInPopupType ? PagePopup : Fragment;

      return {
        path,
        component: <Wrapper>{component}</Wrapper>,
      };
    });

  return (
    <AllPlayersProvider>
      {showVersion && <div className={styles.ChipVersion}>{REACT_APP_VERSION}</div>}
      <Routes>
        {switchRouterList.map((route) => (
          <Route key={route.path} path={route.path} element={<DefaultSuspense>{route.component}</DefaultSuspense>} />
        ))}
        {!lwInPopupType && (
          <Route
            path="*"
            element={
              <DefaultSuspense>
                <ChannelListPage />
              </DefaultSuspense>
            }
          />
        )}
      </Routes>
      {lwInPopupType && (
        <DefaultSuspense>
          <ChannelListPage />
        </DefaultSuspense>
      )}
      {isMobileUserAgent && search && (
        <DefaultSuspense>
          <MobileSearchButton />
        </DefaultSuspense>
      )}
    </AllPlayersProvider>
  );
}

export default AppComponent;
